import { Box, Grid, Typography } from "@drone-js/core";
import { VStack } from "apps/commons/components/Stack";
import { getStartedBinder } from "libs/analytics/binders/propertySelection/getStartedBinder";
import { useAnalytics } from "libs/analytics/useAnalytics";
import { type FunctionComponent, useEffect } from "react";
import { useSearchParams } from "react-router";
import { Footer } from "../Footer";
import { useListNewPropertySourceState } from "../headerAtom";
import { ListNewPropertySource } from "../headerConstants";
import { usePropertySelectionViewModelValue } from "../propertySelectionViewModelAtom";
import { useUnfinishedPropertiesValue } from "../unfinishedPropertiesAtom";
import { useKeyDownHandler } from "../useKeyDownHandler";
import { AutomaticMethod } from "./AutomaticMethod";
import { CampaignNotification } from "./CampaignNotification";
import { ManualMethod } from "./ManualMethod";
import { ResumeMethod } from "./ResumeMethod";
import { ImportOptions } from "./constants";
import { useSetOnboardingType } from "./onboardingTypeAtom";
import { usePaintedDoorModalOpenValue } from "./paintedDoorModalOpenAtom";
import { useOnboardingTypeSelectionHandler } from "./useOnboardingTypeSelectionHandler";

export const HomesOnboardingTypeSelectionPage: FunctionComponent = () => {
    const [, setListNewPropertySourceState] = useListNewPropertySourceState();
    const setOnboardingType = useSetOnboardingType();

    const [searchParams] = useSearchParams();
    const source = searchParams.get("source");

    const {
        campaignBenefits,
        importMethodCms: { bComPageTitle, next, exit, welcomeBackLabel, completeYourListing, startANewListing },
    } = usePropertySelectionViewModelValue();

    const [firstProperty] = useUnfinishedPropertiesValue();

    const { onNext, onExit } = useOnboardingTypeSelectionHandler();
    const modalOpen = usePaintedDoorModalOpenValue();

    useKeyDownHandler(
        (e) => {
            if (e.code === "Enter" && !modalOpen) onNext();
        },
        [onNext, modalOpen],
    );

    useEffect(() => {
        if (!source) setListNewPropertySourceState(ListNewPropertySource.NewUser);
        else setListNewPropertySourceState(+source);

        if (firstProperty?.propertyName) setOnboardingType(ImportOptions.Resume);
    }, []);

    useAnalytics(getStartedBinder);

    return (
        <Box py="32" display="flex" flexDirection="column">
            {campaignBenefits.length > 0 ? (
                <VStack gap="8">
                    <Typography variant="heading-xl" mb="24" textColor="base.default">
                        {bComPageTitle}
                    </Typography>
                    <CampaignNotification />
                </VStack>
            ) : firstProperty?.propertyName ? (
                <>
                    <Typography variant="heading-xl" mb="32" textColor="base.default">
                        {welcomeBackLabel}
                    </Typography>
                    <Box mb="32">
                        <Typography variant="heading-default" mb="24" textColor="base.default">
                            {completeYourListing}
                        </Typography>
                        <Grid>
                            <ResumeMethod />
                        </Grid>
                    </Box>
                    <Typography variant="heading-default" mb="24" textColor="base.default">
                        {startANewListing}
                    </Typography>
                </>
            ) : (
                <Typography variant="heading-xl" mb="32" textColor="base.default">
                    {bComPageTitle}
                </Typography>
            )}
            <Grid spacing="16">
                <AutomaticMethod />
                <ManualMethod />
            </Grid>
            <Footer
                secondary={{
                    label: exit,
                    dataElementName: "exit-button",
                }}
                primary={{
                    label: next,
                    dataElementName: "next",
                }}
                onSecondary={onExit}
                onPrimary={onNext}
            />
        </Box>
    );
};
